import { useState, useEffect } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogoMark } from "../components/Logo";
import PasswordField from "../components/PasswordField";
import { devWarn } from "../lib/logger";

export default function Register() {
  const { register, user } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [refInfo, setRefInfo] = useState(null);
  const nav = useNavigate();

  // Capture ?ref=AMICO-XXXX from URL or localStorage
  useEffect(() => {
    const fromUrl = searchParams.get("ref");
    const fromStorage = typeof window !== "undefined" ? localStorage.getItem("etabuild_ref_code") : null;
    const code = (fromUrl || fromStorage || "").toUpperCase();
    if (!code) return;
    if (fromUrl) localStorage.setItem("etabuild_ref_code", code);
    (async () => {
      try {
        const { data } = await api.get(`/referral/resolve/${code}`);
        setRefInfo(data);
      } catch (e) {
        devWarn("referral resolve failed", e);
        localStorage.removeItem("etabuild_ref_code");
      }
    })();
  }, [searchParams]);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(email, password, name);
      // Redeem referral if code present
      const code = localStorage.getItem("etabuild_ref_code");
      if (code && u?.id) {
        try {
          await api.post("/referral/redeem", { code, referee_user_id: u.id });
          localStorage.removeItem("etabuild_ref_code");
        } catch (e) { devWarn("referral redeem failed (non-blocking)", e); }
      }
      toast.success("Benvenuto in ETA Build! 30 giorni di prova gratuita attivati.");
      nav("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore registrazione");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12">
            <LogoMark size={48} />
          </div>
          <div>
            <div className="font-heading text-3xl font-black uppercase leading-none">ETA<span className="text-[#FFD600]">Build</span></div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mt-1">Inizia oggi · 30 giorni gratis</div>
          </div>
        </div>

        <h1 className="font-heading text-5xl font-black uppercase tracking-tighter leading-none mb-2">Registrati</h1>
        <p className="text-[#A3A3A3] mb-6">Poi <span className="text-[#FFD600] font-bold">da 8€/mese</span>. Standard o Premium. Disdici quando vuoi.</p>

        {refInfo && (
          <div className="bg-[#FFD600]/10 border-2 border-[#FFD600] rounded-md p-3 mb-5" data-testid="ref-banner">
            <div className="text-xs uppercase tracking-wider text-[#FFD600] font-bold mb-1">Invito amico attivo</div>
            <div className="text-sm text-white">
              <b>{refInfo.referrer_name}</b> ti regala <b className="text-[#FFD600]">{refInfo.reward_percent}%</b> di sconto per {refInfo.reward_months} mesi
            </div>
            <div className="text-[10px] text-[#A3A3A3] font-mono mt-1">Codice: {refInfo.code}</div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-[#A3A3A3] uppercase tracking-wider font-bold mb-2 block">Nome e Cognome</label>
            <input data-testid="reg-name" required value={name} onChange={(e)=>setName(e.target.value)}
              className="w-full bg-[#1A1A1A] border-2 border-[#333] h-14 text-lg text-white rounded-md px-4 focus:border-[#FFD600] focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-[#A3A3A3] uppercase tracking-wider font-bold mb-2 block">Email</label>
            <input data-testid="reg-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
              className="w-full bg-[#1A1A1A] border-2 border-[#333] h-14 text-lg text-white rounded-md px-4 focus:border-[#FFD600] focus:outline-none" />
          </div>
          <PasswordField label="Password" dataTestId="reg-password" value={password} onChange={(e)=>setPassword(e.target.value)} minLength={6} />
          <button data-testid="reg-submit" disabled={loading} type="submit"
            className="w-full bg-[#FFD600] text-[#0A0A0A] h-16 text-xl font-black uppercase tracking-tight rounded-md hover:bg-[#E6C200] transition disabled:opacity-50">
            {loading ? "Caricamento..." : "Inizia 30 giorni gratis"}
          </button>
        </form>
        <div className="mt-6 text-center text-[#A3A3A3]">
          Hai già un account? <Link to="/login" className="text-[#FFD600] font-bold uppercase" data-testid="goto-login">Accedi</Link>
        </div>
      </div>
    </div>
  );
}
